import type { MfgUnit } from "@/lib/manufacturingClient";
import type { MfgBuildPipelinePhase } from "@/lib/manufacturing/buildPipelineTypes";
import type { MfgProductionLoad } from "@/lib/manufacturing/productionLoadsClient";
import { productionLoadLabel } from "@/lib/manufacturing/productionLoadsClient";
import { canMoveUnitToPhase } from "@/lib/manufacturing/buildPipelinePhaseAccess";
import {
  handoffAgeLabel,
  unitHandoffSerial,
  unitHandoffTitle,
} from "@/lib/ops/handoffIdentity";
import { FadeScrollArea } from "@/components/common/FadeScrollArea";

/** ~8 compact serial rows before column scroll kicks in. */
export const BUILD_PIPELINE_COLUMN_MAX_H_CLASS = "max-h-[18rem]";

export function BuildPipelineUnitCard({
  unit,
  phases,
  loads,
  actorRoles,
  moveBusy,
  onOpen,
  onDragStart,
  onDragEnd,
}: {
  unit: MfgUnit;
  phases: MfgBuildPipelinePhase[];
  loads: MfgProductionLoad[];
  actorRoles: readonly string[];
  moveBusy: boolean;
  onOpen: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const currentId = unit.current_phase_id;
  const sorted = [...phases].sort((a, b) => a.sequence_index - b.sequence_index);
  const currentIdx = sorted.findIndex((p) => p.id === currentId);
  const fromPhase =
    currentIdx >= 0 ? sorted[currentIdx] : sorted.find((p) => p.is_initial) ?? null;
  const prev = currentIdx > 0 ? sorted[currentIdx - 1] : null;
  const next =
    currentIdx >= 0 && currentIdx < sorted.length - 1 ? sorted[currentIdx + 1] : null;

  const canPrev = prev ? canMoveUnitToPhase(actorRoles, fromPhase, prev) : false;
  const canNext = next ? canMoveUnitToPhase(actorRoles, fromPhase, next) : false;
  const movable = canPrev || canNext;
  const load = loads.find((l) => l.id === unit.production_load_id) ?? null;
  const title = unitHandoffTitle(unit);
  const serial = unitHandoffSerial(unit);
  const place = unit.seller_location_name?.trim() || "";
  const age = handoffAgeLabel(unit.created_at);
  const detail = [serial ?? "No serial yet", place, age, unit.build_completed_at ? "Plant finished" : ""]
    .filter(Boolean)
    .join(" · ");

  return (
    <li
      draggable={movable && !moveBusy}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", unit.id);
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-left shadow-sm dark:border-zinc-700 dark:bg-zinc-900"
      style={{ cursor: movable && !moveBusy ? "grab" : "pointer", opacity: moveBusy ? 0.55 : 1 }}
      title={unit.serial?.trim() || unit.display_label}
    >
      <button type="button" className="w-full text-left" onClick={onOpen}>
        <div className="truncate text-xs font-semibold text-gray-900 dark:text-zinc-100">{title}</div>
        <div className="truncate text-[10px] text-gray-500 dark:text-zinc-400">{detail}</div>
      </button>
      {load ? (
        <div className="mt-0.5 truncate text-[10px] font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
          {productionLoadLabel(load)}
        </div>
      ) : null}
    </li>
  );
}

export function BuildPipelineColumn({
  phase,
  units,
  phases,
  loads,
  actorRoles,
  moveBusyId,
  dragOver,
  onOpenUnit,
  onDragStartUnit,
  onDragEndUnit,
  onDropUnit,
  onDragOverPhase,
  onDragLeavePhase,
}: {
  phase: MfgBuildPipelinePhase;
  units: MfgUnit[];
  phases: MfgBuildPipelinePhase[];
  loads: MfgProductionLoad[];
  actorRoles: readonly string[];
  moveBusyId: string | null;
  dragOver: boolean;
  onOpenUnit: (unitId: string) => void;
  onDragStartUnit: (unitId: string) => void;
  onDragEndUnit: () => void;
  onDropUnit: (toPhaseId: string) => void;
  onDragOverPhase: (phaseId: string) => void;
  onDragLeavePhase: (phaseId: string) => void;
}) {
  return (
    <section
      className="flex w-56 shrink-0 flex-col rounded-xl border bg-gray-50/80 dark:bg-zinc-950/60"
      style={{
        borderColor: dragOver ? "rgb(79 70 229)" : undefined,
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOverPhase(phase.id);
      }}
      onDragLeave={() => onDragLeavePhase(phase.id)}
      onDrop={(e) => {
        e.preventDefault();
        onDropUnit(phase.id);
      }}
    >
      <header className="border-b border-gray-200 px-3 py-2 dark:border-zinc-700">
        <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
          {phase.display_name}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          <span>{units.length}</span>
          {phase.is_initial ? <span>· initial</span> : null}
          {phase.is_build_complete ? <span>· build complete</span> : null}
          {phase.is_pay_trigger ? <span>· pay</span> : null}
        </div>
      </header>
      <FadeScrollArea
        axis="vertical"
        contentClassName={`${BUILD_PIPELINE_COLUMN_MAX_H_CLASS} p-2`}
      >
        <ul className="flex flex-col gap-1.5">
          {units.length === 0 ? (
            <li className="px-1 py-3 text-xs text-gray-500 dark:text-zinc-500">
              {phase.is_initial ? "No new orders" : "Empty"}
            </li>
          ) : (
            units.map((u) => (
              <BuildPipelineUnitCard
                key={u.id}
                unit={u}
                phases={phases}
                loads={loads}
                actorRoles={actorRoles}
                moveBusy={moveBusyId === u.id}
                onOpen={() => onOpenUnit(u.id)}
                onDragStart={() => onDragStartUnit(u.id)}
                onDragEnd={onDragEndUnit}
              />
            ))
          )}
        </ul>
      </FadeScrollArea>
    </section>
  );
}
